// Live console feed: reading a container analysis's telemetry.
//
// The screen's twin (lib/screen.ts), and one-way for the same reason: a fetch()
// body reader has nowhere to write, so nothing on this channel can reach the
// page. Driving the page is the input route's job, on its own transport.
//
// Each line is one message exactly as the analyzer's backend wrote it. The
// platform relays that vocabulary and never reads inside it, so this file does
// not either: it splits lines and parses JSON, and the console that consumes the
// messages is the thing that knows what they mean. A blank line is a keepalive.

/**
 * How a feed ended, in the words the console acts on.
 *
 * 'unavailable' is the one to retry: the run is live and its console is not up
 * yet, which is the ordinary state in its first seconds. 'not-running' means the
 * run is over and the report is the place to look. 'not-applicable' is a run
 * with no live console at all, and 'refused' a caller who may not watch it.
 * 'busy' is the viewer limit, every live view this caller may hold being open:
 * retried, since it clears when one closes, and said.
 */
export type FeedStatus = 'ended' | 'unavailable' | 'busy' | 'not-applicable' | 'not-running' | 'refused';

/**
 * Reads the feed, calling onMessage for each message until the response ends or
 * the signal aborts. Resolves with the reason it ended; a clean end is normal.
 */
export async function readFeedStream(
  submissionId: string,
  onMessage: (message: unknown) => void,
  signal: AbortSignal,
): Promise<FeedStatus> {
  let resp: Response;
  try {
    resp = await fetch(`/api/submissions/${submissionId}/feed`, { credentials: 'include', signal });
  } catch {
    return signal.aborted ? 'ended' : 'unavailable';
  }
  // Answered the same way on every retry, so the console stops rather than
  // reopening the stream every few seconds for as long as the tab stays open.
  if (resp.status === 401 || resp.status === 403 || resp.status === 404) return 'refused';
  if (resp.status === 422) return 'not-applicable';
  if (resp.status === 409) return 'not-running';
  if (resp.status === 429) return 'busy';
  // 425 is the run's first seconds, before its backend has advertised itself,
  // and 503 anything else that is "not now". Both are the retry.
  if (!resp.ok || !resp.body) return 'unavailable';

  const reader = resp.body.getReader();
  // Chunk boundaries have nothing to do with line boundaries, and a multi-byte
  // character can be split across two reads, so bytes are decoded in stream
  // mode and a partial line waits for the rest of itself.
  const decoder = new TextDecoder();
  let pending = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) return 'ended';
      if (!value) continue;
      pending += decoder.decode(value, { stream: true });
      let newline = pending.indexOf('\n');
      while (newline >= 0) {
        const line = pending.slice(0, newline).trim();
        pending = pending.slice(newline + 1);
        if (line !== '') {
          try {
            onMessage(JSON.parse(line));
          } catch {
            // One unreadable message must not end a live console; the next one
            // is as likely as ever to be fine. The provider already refuses any
            // line that is not a JSON object, so this is not a path in practice.
          }
        }
        newline = pending.indexOf('\n');
      }
    }
  } catch {
    return signal.aborted ? 'ended' : 'unavailable';
  } finally {
    reader.cancel().catch(() => {});
  }
}
